import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, Space } from 'antd';
import {
  SendOutlined,
  RobotOutlined,
  CloseOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import api from '../../libs/api';

/**
 * Floating AI ChatBox component.
 * Props:
 *   - endpoint: backend chat endpoint (e.g. '/api/xero/chat')
 *   - contextBuilder: () => string — returns current data context for AI
 *   - systemPromptExtra: string — extra instructions for the AI system prompt
 *   - title: string — chatbox header title (default: 'AI Analyst')
 *   - placeholder: string — input placeholder text
 */
export default function AIChatBox({
  endpoint = '/api/xero/chat',
  contextBuilder,
  systemPromptExtra = '',
  title = 'AI Analyst',
  placeholder = 'Ask about this data...',
}) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const userMsg = { role: 'user', content: text };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput('');
    setLoading(true);
    try {
      const reportContext = contextBuilder ? contextBuilder() : '';
      const res = await api.post(endpoint, {
        messages: updated,
        reportContext: reportContext + (systemPromptExtra ? `\n\n${systemPromptExtra}` : ''),
      });
      const reply = res.data.data?.message;
      if (reply) setMessages(prev => [...prev, reply]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, analysis failed. Please try again.' }]);
    }
    setLoading(false);
  };

  if (!open) {
    return (
      <Button
        type="primary"
        shape="circle"
        size="large"
        icon={<MessageOutlined />}
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
          width: 56, height: 56, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          background: '#54A2CC',
        }}
      />
    );
  }

  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
      width: 420, height: 520, display: 'flex', flexDirection: 'column',
      background: '#fff', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      border: '1px solid #e8e8e8',
    }}>
      <div style={{
        padding: '12px 16px', background: '#54A2CC', borderRadius: '12px 12px 0 0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Space>
          <RobotOutlined style={{ color: '#fff', fontSize: 18 }} />
          <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{title}</span>
        </Space>
        <Button type="text" size="small" icon={<CloseOutlined style={{ color: '#fff' }} />} onClick={() => setOpen(false)} />
      </div>

      <div ref={listRef} style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: '#999', marginTop: 40, fontSize: 13 }}>
            <RobotOutlined style={{ fontSize: 32, color: '#ccc', display: 'block', marginBottom: 12 }} />
            {placeholder}
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} style={{
            marginBottom: 12, display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
          }}>
            <div style={{
              maxWidth: '85%', padding: '8px 12px', borderRadius: 10, fontSize: 13, lineHeight: 1.5,
              background: msg.role === 'user' ? '#54A2CC' : '#f5f5f5',
              color: msg.role === 'user' ? '#fff' : '#333',
              whiteSpace: 'pre-wrap',
            }}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
            <div style={{ padding: '8px 12px', borderRadius: 10, background: '#f5f5f5', color: '#999', fontSize: 13 }}>
              Analysing...
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: '8px 12px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: 8 }}>
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          onPressEnter={sendMessage}
          placeholder={placeholder}
          size="small"
          disabled={loading}
        />
        <Button type="primary" size="small" icon={<SendOutlined />} onClick={sendMessage} loading={loading}
          style={{ background: '#54A2CC', borderColor: '#54A2CC' }} />
      </div>
    </div>
  );
}
